import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { hasPermission } from "../../../../utils/helpers";

export const SPEC_MANAGER_TAB_KEYS = {
  PRODUCT: "product",
  TEST: "test",
};

export const ENV_SPEC_MANAGER_TAB_KEYS = {
  LOCATION: "location",
  TEST: "test",
};

export const SPEC_FILTER_VALUES = {
  ALL: "",
  ADDED: "added",
  PENDING: "pending",
};

export const GLOBAL_TEST_SPEC_MODE = {
  LIST: "list",
  VIEW: "view",
  ADD: "add",
  EDIT: "edit",
};

export const SPEC_TYPE = {
  PRODUCT: "product",
  ENVIRONMENT: "environment",
};

const TEST_SPEC_ENTITY_LABELS = {
  [SPEC_TYPE.PRODUCT]: {
    plural: "Products",
    attachedTitle: "Attached Products",
    selectedPreviewTitle: "Selected Products",
    emptyCanEdit: "No products are currently using this standard. Click Edit Spec to add products.",
    emptyViewOnly: "No products are currently using this standard.",
    totalLabel: "Total Products",
    standardLabel: "Products following standard spec",
    customLabel: "Products following custom spec",
    standardTooltip: "This product follows the standard spec for this test.",
    customTooltip: "This product follows a custom spec defined for that product.",
    showCustomCoverage: true,
    showCustomBadges: true,
  },
  [SPEC_TYPE.ENVIRONMENT]: {
    plural: "Locations",
    attachedTitle: "Attached Locations",
    selectedPreviewTitle: "Selected Locations",
    emptyCanEdit: "No locations are currently using this standard. Click Edit Spec to add locations.",
    emptyViewOnly: "No locations are currently using this standard.",
    totalLabel: "Total Locations",
    standardLabel: "Locations following standard spec",
    customLabel: "Locations following custom spec",
    standardTooltip: "This location follows the standard spec for this test.",
    customTooltip: "This location follows a custom spec defined for that location.",
    showCustomCoverage: true,
    showCustomBadges: true,
  },
};

/**
 * Returns the labels for a test spec entity based on the spec type.
 * @param {SPEC_TYPE} specType - The type of spec entity to get labels for.
 * @returns {Object} The labels for the spec entity.
 */
export const getTestSpecEntityLabels = (specType = SPEC_TYPE.PRODUCT) => (
  TEST_SPEC_ENTITY_LABELS[specType] || TEST_SPEC_ENTITY_LABELS[SPEC_TYPE.PRODUCT]
);

/** ESV equivalent: permissions.environment.specsManager / permissions.product.specManager */
export const canViewSpecManager = () => hasPermission("view_automation_dashboard_esv_specs");

/** ESV equivalent: hasRolePermission("add_spec") */
export const canEditSpecs = () => hasPermission("edit_automation_dashboard_esv_specs");

export const UNIT_OPTIONS = ["None", "CFU/g", "CFU", "mg/kg", "ppb", "ppm", "%", "g", "ml"];

/**
 * Returns unit options from props, or UNIT_OPTIONS before the API loads.
 */
export const getUnitOptions = (unitOptionsProp) => (
  (unitOptionsProp && unitOptionsProp.length) ? unitOptionsProp : UNIT_OPTIONS
);

/** Canonical qualitative / quantitative / range labels (single source of truth). */
export const TEST_TYPE_DISPLAY_LABELS = Object.freeze({
  qualitative: "Qualitative",
  quantitative: "Quantitative",
  range: "Range",
});

/** List / table subtext when a test has no standard spec payload to display (matches GlobalSpecTable). */
export const TEST_SPEC_SUBTITLE_NOT_CONFIGURED_LABEL = "Not Configured";

/**
 * @param {unknown} raw API or UI field, any casing
 * @returns {"qualitative"|"quantitative"|"range"|""}
 */
export const normalizeStandardTestTypeKey = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  const key = raw.toLowerCase();
  return Object.prototype.hasOwnProperty.call(TEST_TYPE_DISPLAY_LABELS, key) ? key : "";
};

/**
 * @param {unknown} raw
 * @returns {string} Display label, or "" if not a standard test type
 */
export const getTestTypeDisplayLabel = (raw) => {
  const key = normalizeStandardTestTypeKey(raw);
  return key ? TEST_TYPE_DISPLAY_LABELS[key] : "";
};

/**
   * Remove commas from string, parseFloat to get a number
   * @param {String} value
   * @returns {Number} return number parse from string
   */
export const parseNumber = (value) => parseFloat(value.split(",").join(""));

/**
   * Check if value is a number
   * @param {String} value
   * @returns {Boolean} true if a number else false
   */
export const isNumber = (value) => !Number.isNaN(parseNumber(value));

/**
 * True when the string has only digits, commas, and at most one period.
 * @param {String} string input value
 * @returns {Boolean} true if valid, else false
 */
export const isValid = (string) => /^([0-9,]|(\.(?![^\\.]*\.+[^\\.]*)))*$/.test(string);

/**
 * Selected MRL market codes from the editor row shape `countries_lst` (see {@link formatStandardSpecsListSubtitle} for list API parity).
 * Supports legacy `string[]` (same as globalspecs list `countries_list`) and `{ country, selected? }[]` (omitted `selected` => treated as selected).
 * @param {{ countries_lst?: unknown }|null|undefined} spec
 * @returns {string[]}
 */
export const getSelectedMrlMarketsFromSpecRow = (spec) => {
  const lst = spec && spec.countries_lst;
  if (!Array.isArray(lst) || lst.length === 0) return [];
  if (lst.every((entry) => typeof entry === "string")) {
    return lst
      .map((countryCode) => String(countryCode).trim())
      .filter(Boolean);
  }
  return lst
    .filter((countryEntry) => {
      if (countryEntry == null || typeof countryEntry !== "object") return false;
      if (!Object.prototype.hasOwnProperty.call(countryEntry, "selected")) return true;
      return countryEntry.selected === true;
    })
    .map((countryEntry) => String(countryEntry.country != null ? countryEntry.country : "").trim())
    .filter(Boolean);
};

export const formatSpec = (dataOfSpecEditing, skipCountryList = false) => dataOfSpecEditing.map((element) => {
  const test_type = element.test_type;
  const specs = element.specs;
  const test = element.test;
  let min = "";
  let max = "";
  let unit = "";
  if (test_type === "range") {
    if ((specs[0] && !specs[1]) || (!specs[0] && specs[1])) {
      return "Range is missing a value";
    }
    if (specs[0] && specs[1] && specs[0].trim() && specs[1].trim()) {
      if (!isValid(specs[0]) || !isValid(specs[1])) {
        return "Invalid input";
      }
      const numFrom = parseNumber(specs[0]);
      const numTo = parseNumber(specs[1]);

      if (isNumber(specs[0]) && isNumber(specs[1])) {
        specs[2] = !specs[2] ? "" : specs[2];
        if (numFrom > numTo) {
          min = specs[1].trim();
          max = specs[0].trim();
        } else {
          min = specs[0].trim();
          max = specs[1].trim();
        }
      } else {
        return "Range is missing a value";
      }
    }
  } else if (test_type === "quantitative") {
    if (specs[1] && specs[1].trim()) {
      if (!isValid(specs[1])) {
        return "Invalid input";
      }
      if (!specs[2]) {
        specs[2] = "None";
      }
      if (isNumber(specs[1])) {
        if (!specs[0] || specs[0] === "Max") {
          max = `<${specs[1].trim()}`;
        } else {
          min = `>${specs[1].trim()}`;
          max = "";
        }
      }
    }
  } else if (test_type === "qualitative") {
    if (specs[1] && specs[1].trim()) {
      specs[0] = !specs[0] ? "Negative" : specs[0];
      specs[1] = specs[1].trim()[0] === "/" ? specs[1].trim() : `/${specs[1].trim()}`;
      if (!isValid(specs[1].substring(1))) {
        return "Invalid input";
      }
      if (isNumber(specs[1].substring(1))) {
        max = specs[0] + specs[1];
      }
    } else {
      max = "Negative";
    }
  }
  if (element.specs[2] === "None") {
    element.specs[2] = "";
  }
  if (!max.includes("/") && test_type === "qualitative") {
    unit = "";
  } else {
    unit = element.specs[2] || "";
  }

  const formattedValue = {
    test, min, max, unit,
  };

  // Pesticide / MRL: must match {@link getSelectedMrlMarketsFromSpecRow} (omitted `selected` => included).
  if (element.countries_lst && !skipCountryList) {
    formattedValue.countries_lst = getSelectedMrlMarketsFromSpecRow(element);
  }

  return formattedValue;
});

export const parseSpecDetail = (spec) => {
  let specs = ["", "", ""];
  let test_type = "";
  const test = spec.test ? spec.test : "";

  if (spec.min !== undefined && spec.max !== undefined) {
    const specString = spec.min + spec.max;

    if (specString.startsWith("Positive")) {
      test_type = "qualitative";
      const numberString = specString.split("/")[1];
      specs = ["Positive", numberString, spec.unit];
    } else if (specString.startsWith("Negative")) {
      test_type = "qualitative";
      const numberString = specString.split("/")[1];
      specs = ["Negative", numberString, spec.unit];
    } else if (specString[0] === "<" || specString[0] === ">") {
      const maxString = specString[0] === "<" ? "Max" : "Min";
      let numberString = specString.substring(1);
      const unitFromSpec = (spec.unit != null ? spec.unit : "").trim();
      if (unitFromSpec && specString.endsWith(unitFromSpec)) {
        numberString = numberString.slice(0, -unitFromSpec.length);
      }
      test_type = "quantitative";
      specs = [maxString, numberString, spec.unit];
    } else if (specString === "") {
      test_type = "";
    } else {
      const minStr = (spec.min != null ? spec.min : "").trim();
      const maxStr = (spec.max != null ? spec.max : "").trim();
      const hasOnlyMin = Boolean(minStr) && !maxStr;
      const hasOnlyMax = !minStr && Boolean(maxStr);
      if ((hasOnlyMin || hasOnlyMax) && specString !== "None") {
        test_type = "quantitative";
        specs = [hasOnlyMax ? "Max" : "Min", hasOnlyMax ? maxStr : minStr, spec.unit != null ? spec.unit : ""];
      } else if (specString !== "None") {
        test_type = "range";
        if (specString.endsWith("%")) {
          spec.min = spec.min.substring(0, spec.min.length - 1);
          spec.max = spec.max.substring(0, spec.max.length - 1);
          specs[2] = spec.unit;
        }
        specs[0] = spec.min;
        specs[1] = spec.max;
        specs[2] = spec.unit;
      }
    }
  }
  const parsedValues = {
    test,
    test_type,
    specs,
    removable: spec.removable,
  };
  if (spec.countries_lst) {
    parsedValues.countries_lst = spec.countries_lst;
  }
  return parsedValues;
};

/**
 * Build editor-shaped spec rows from globalspecs detail `result`.
 * Mirrors ESV GlobalTestSpec.openTestSpecEditor — trusts API shape on success.
 */
export const buildGlobalSpecEditorRowsFromApi = (apiResult, testName) => {
  const emptySpec = {
    test: testName,
    test_type: "",
    specs: ["", "", ""],
    removable: true,
  };

  const {
    test, min, max, unit, countries_list: countriesList,
  } = apiResult;
  const countriesLst = countriesList != null ? countriesList : [];
  const spec = {
    test,
    min: min != null ? min : "",
    max: max != null ? max : "",
    unit: unit != null ? unit : "",
    countries_lst: countriesLst,
  };
  const hasSpecValues = min || max || unit;
  const hasMrlCountries = Array.isArray(countriesLst) && countriesLst.length > 0;
  if (hasSpecValues || hasMrlCountries) {
    return [parseSpecDetail(spec)];
  }
  return [emptySpec];
};

/**
 * Infer qualitative / quantitative / range from globalspecs list payload `standard_specs`
 * (same rules as parseSpecDetail on min/max/unit).
 * @param {{ min?: string, max?: string, unit?: string }|null|undefined} standardSpecs
 * @returns {"qualitative"|"quantitative"|"range"|""}
 */
export const inferTestTypeFromStandardSpecs = (standardSpecs) => {
  if (!standardSpecs || typeof standardSpecs !== "object") return "";
  const parsed = parseSpecDetail({
    test: "",
    min: standardSpecs.min != null ? standardSpecs.min : "",
    max: standardSpecs.max != null ? standardSpecs.max : "",
    unit: standardSpecs.unit != null ? standardSpecs.unit : "",
  });
  return parsed.test_type || "";
};

/**
 * Selected country codes from globalspecs list `standard_specs.countries_list`:
 * legacy string[], or `{ country, selected }[]` (only `selected === true`; if `selected` omitted, include).
 * @param {{ countries_list?: unknown }|null|undefined} standardSpecs
 * @returns {string[]}
 */
export const getSelectedCountryCodesFromStandardSpecsList = (standardSpecs) => {
  const countriesListRaw = standardSpecs && standardSpecs.countries_list;
  if (!Array.isArray(countriesListRaw) || countriesListRaw.length === 0) return [];
  if (countriesListRaw.every((entry) => typeof entry === "string")) {
    return countriesListRaw
      .map((countryCode) => String(countryCode).trim())
      .filter(Boolean);
  }
  return countriesListRaw
    .filter((countryEntry) => {
      if (countryEntry == null || typeof countryEntry !== "object") return false;
      if (!Object.prototype.hasOwnProperty.call(countryEntry, "selected")) return true;
      return countryEntry.selected === true;
    })
    .map((countryEntry) => String(countryEntry.country != null ? countryEntry.country : "").trim())
    .filter(Boolean);
};

/**
 * Append unit to a spec fragment when the API keeps unit separate (e.g. max "<10" + unit "CFU/g").
 */
const appendUnitSuffixIfMissing = (specFragment, unitSuffix) => {
  const trimmedFragment = (specFragment != null ? specFragment : "").trim();
  const trimmedUnit = (unitSuffix != null ? unitSuffix : "").trim();
  if (!trimmedFragment) return trimmedUnit;
  if (!trimmedUnit) return trimmedFragment;
  if (trimmedFragment.endsWith(trimmedUnit)) return trimmedFragment;
  return `${trimmedFragment} ${trimmedUnit}`.trim();
};

/**
 * Bounds/value fragment for API-shaped `standard_specs` (shared by list subtitles and editor preview).
 * @param {{ min?: string, max?: string, unit?: string }} standardSpecs
 * @param {"qualitative"|"quantitative"|"range"|""} testType
 * @returns {string}
 */
export const formatStandardSpecsValueFragment = (standardSpecs, testType) => {
  if (!standardSpecs || typeof standardSpecs !== "object") return "";
  const unitSuffix = (standardSpecs.unit != null ? standardSpecs.unit : "").trim();

  if (testType === "range") {
    const rangeMin = (standardSpecs.min != null ? standardSpecs.min : "").trim();
    const rangeMax = (standardSpecs.max != null ? standardSpecs.max : "").trim();
    const specFragment = [rangeMin, rangeMax].filter(Boolean).join(" – ");
    return appendUnitSuffixIfMissing(specFragment, unitSuffix);
  }
  if (testType === "quantitative") {
    const minBound = (standardSpecs.min != null ? standardSpecs.min : "").trim();
    const maxBound = (standardSpecs.max != null ? standardSpecs.max : "").trim();
    return appendUnitSuffixIfMissing(minBound || maxBound, unitSuffix);
  }
  if (testType === "qualitative") {
    const qualitativeMax = (standardSpecs.max != null ? standardSpecs.max : "").trim();
    const specFragment = qualitativeMax
      ? appendUnitSuffixIfMissing(qualitativeMax, unitSuffix)
      : "Negative";
    return specFragment;
  }
  const combinedBounds = `${standardSpecs.min != null ? standardSpecs.min : ""}${standardSpecs.max != null ? standardSpecs.max : ""}`.trim();
  return appendUnitSuffixIfMissing(combinedBounds, unitSuffix);
};

/**
 * Subtitle for Test Spec list rows when API sends `standard_specs`.
 * @param {{
 *   min: string,
 *   max: string,
 *   unit: string,
 *   specs_type: string,
 *   countries_list: Array<string | { country?: string, selected?: boolean }>,
 * }|null} standardSpecs
 * @param {"qualitative"|"quantitative"|"range"|""} testType
 * @returns {string}
 */
export const formatStandardSpecsListSubtitle = (standardSpecs, testType) => {
  if (!standardSpecs || typeof standardSpecs !== "object") return "";

  const specFragment = formatStandardSpecsValueFragment(standardSpecs, testType);

  const hasCountriesPayload = Array.isArray(standardSpecs.countries_list)
    && standardSpecs.countries_list.length > 0;
  const selectedCountryCodes = getSelectedCountryCodesFromStandardSpecsList(standardSpecs);
  const countriesSummaryLine = selectedCountryCodes.length
    ? selectedCountryCodes.join(", ")
    : "";

  const typeDisplayLabel = getTestTypeDisplayLabel(testType);

  let specSummaryPart = [
    typeDisplayLabel && specFragment ? `${typeDisplayLabel}: ${specFragment}` : typeDisplayLabel || specFragment,
  ].filter(Boolean).join("");

  if (!specSummaryPart && hasCountriesPayload) {
    specSummaryPart = "MRL";
  }

  if (!specSummaryPart && !countriesSummaryLine) return "";
  // List rule: either standard type + value **or** MRL + markets — never both in one subtitle.
  if (countriesSummaryLine) {
    return countriesSummaryLine;
  }
  return specSummaryPart;
};

/**
 * Spec summary for UI preview from editor shape `{ test_type, specs }`.
 * Uses {@link formatSpec} (same as save) then {@link formatStandardSpecsValueFragment} (same as list rows).
 * Caller handles MRL-only presentation. Returns "Not defined" when incomplete or unknown type.
 * @param {{ test_type?: string, specs?: string[] }} spec
 * @returns {string}
 */
export const formatSpecValueFromEditorShape = (spec = {}) => {
  const normalizedTypeKey = normalizeStandardTestTypeKey(spec.test_type);
  const specForFormat = {
    ...spec,
    test_type: normalizedTypeKey || spec.test_type || "",
    specs: Array.isArray(spec.specs) ? [...spec.specs] : [],
  };
  const formatted = formatSpec([specForFormat]);
  const row = formatted[0];
  if (row == null || typeof row === "string") return "Not defined";

  const resolvedType = normalizedTypeKey || inferTestTypeFromStandardSpecs(row);
  if (!resolvedType) return "Not defined";

  const fragment = formatStandardSpecsValueFragment(row, resolvedType);
  return (fragment && fragment.trim()) || "Not defined";
};

/**
 * Full list-row subtitle: handles missing/null `standard_specs` and empty formatted specs.
 * @param {{ standard_specs?: unknown, test_type?: string }} listRow globalspecs list item
 * @returns {string}
 */
export const buildGlobalTestSpecListSubtitle = (listRow) => {
  const hasStandardSpecsKey = Object.prototype.hasOwnProperty.call(listRow, "standard_specs");
  if (!hasStandardSpecsKey || listRow.standard_specs == null) {
    return TEST_SPEC_SUBTITLE_NOT_CONFIGURED_LABEL;
  }
  const resolvedTestType = normalizeStandardTestTypeKey(listRow.test_type)
    || inferTestTypeFromStandardSpecs(listRow.standard_specs);
  return formatStandardSpecsListSubtitle(listRow.standard_specs, resolvedTestType)
    || TEST_SPEC_SUBTITLE_NOT_CONFIGURED_LABEL;
};

export const handleCheckUnit = (specList) => {
  specList.forEach((element) => {
    if (!element.min && !element.max) {
      element.unit = "";
    }
  });
  return specList;
};

/**
 * True when editor-shaped global test spec rows match semantically.
 * Falls back to raw deep equality when formatting yields error strings (incomplete edit).
 * @param {unknown} currentEditorRows
 * @param {unknown} baselineEditorRows
 */
export const globalTestSpecEditorRowsMatchForDraft = (
  currentEditorRows,
  baselineEditorRows,
) => {
  if (!Array.isArray(currentEditorRows) || !Array.isArray(baselineEditorRows)) {
    return currentEditorRows === baselineEditorRows;
  }
  if (currentEditorRows.length !== baselineEditorRows.length) return false;
  const formattedCurrentRows = formatSpec(cloneDeep(currentEditorRows));
  const formattedBaselineRows = formatSpec(cloneDeep(baselineEditorRows));
  if (
    formattedCurrentRows.some((item) => typeof item === "string")
    || formattedBaselineRows.some((item) => typeof item === "string")
  ) {
    return isEqual(cloneDeep(currentEditorRows), cloneDeep(baselineEditorRows));
  }
  return isEqual(
    handleCheckUnit(cloneDeep(formattedCurrentRows)),
    handleCheckUnit(cloneDeep(formattedBaselineRows)),
  );
};

export const checkIfSpecsComplete = (specList) => {
  const formatted = formatSpec(specList);
  let complete = true;

  formatted.forEach((item) => {
    if (item === "Range is missing a value") {
      complete = false;
      return;
    }
    if (!item.min.includes(">") && !item.max.includes("<") && !item.max.includes("Negative") && !item.max.includes("Positive")) {
      if (item.min === "" && item.max === "") {
        complete = false;
      }
    }
  });

  return complete;
};

/**
 * @param {Array} specList
 * @param {boolean} allowEmpty - when true (product spec), empty specs are valid; only invalid/partial disables
 */
export const checkIfSpecsInvalid = (specList, allowEmpty = false) => {
  const formatted = formatSpec(specList);
  return formatted.some((item, i) => {
    if (item === "Invalid input") return true;
    if (item === "Range is missing a value") {
      if (allowEmpty) {
        const spec = specList[i];
        const s = (spec && spec.specs) || [];
        const bothEmpty = !(s[0] && s[0].trim()) && !(s[1] && s[1].trim());
        return !bothEmpty;
      }
      return true;
    }
    return false;
  });
};

/**
 * Test Spec list row style for MRL markets from editor `countries_lst`
 * (matches {@link formatStandardSpecsListSubtitle} when `countriesSummaryLine` is set: comma-separated codes).
 * @param {unknown[]|undefined} countriesLst
 * @param {{ includeMrlPrefix?: boolean }} [options] when true (default), prefix selected markets with `MRL · ` (e.g. edit preview).
 * @returns {string}
 */
export const formatMrlCountriesListSubtitleFromEditorShape = (countriesLst, options = {}) => {
  const { includeMrlPrefix = true } = options;
  if (!Array.isArray(countriesLst) || countriesLst.length === 0) return "";
  const codes = getSelectedMrlMarketsFromSpecRow({ countries_lst: countriesLst });
  if (codes.length > 0) {
    const line = codes.join(", ");
    return includeMrlPrefix ? `MRL · ${line}` : line;
  }
  return "MRL";
};

/**
 * True when the globalspec editor has at least one row with a non-empty `countries_lst` (API MRL market list).
 * This is the data-driven definition of “this save concerns MRL markets”: it must stay aligned with
 * {@link buildGlobalSpecsUpdateRequestBody}’s `includeMrlCountriesList` and with read-only MRL display
 * (e.g. {@link TestSpecOverview}), which are not gated on `special_pesticides`.
 * @param specList
 */
export const globalTestSpecEditorHasMrlCountryListData = (specList) => Array.isArray(specList)
  && specList.some(
    (item) => item && item.countries_lst && Array.isArray(item.countries_lst) && item.countries_lst.length > 0,
  );

/**
 * True when any editor row carries MRL `countries_lst` but no market is selected (all explicit `selected: false`, or empty codes).
 * @param specList
 */
export const globalTestSpecMrlMissingSelectedMarkets = (specList) => {
  if (!Array.isArray(specList)) return false;
  return specList.some((spec) => {
    const lst = spec && spec.countries_lst;
    if (!Array.isArray(lst) || lst.length === 0) return false;
    return getSelectedMrlMarketsFromSpecRow(spec).length === 0;
  });
};

/**
 * True when the spec list includes MRL country data and save / continue should be blocked (invalid numeric fields or no market selected).
 * @param {unknown[]} specList
 */
export const isGlobalTestSpecMrlSaveBlocked = (specList) => {
  if (!globalTestSpecEditorHasMrlCountryListData(specList)) return false;
  return checkIfSpecsInvalid(specList) || globalTestSpecMrlMissingSelectedMarkets(specList);
};

/**
 * Whether Global Test Spec primary save/update should be disabled due to spec form state.
 * MRL rows use `countries_lst`, not min/max — {@link checkIfSpecsComplete} alone would keep them "incomplete".
 */
export const globalTestSpecSpecFormBlocksSave = (specList) => {
  if (!Array.isArray(specList) || specList.length === 0) return true;
  if (checkIfSpecsInvalid(specList)) return true;
  if (isGlobalTestSpecMrlSaveBlocked(specList)) return true;
  if (globalTestSpecEditorHasMrlCountryListData(specList)) return false;
  return !checkIfSpecsComplete(specList);
};

/**
 * Returns true if specs are empty, invalid, or incomplete.
 * @param {boolean} allowEmpty - when true (product spec), only invalid disables; empty is allowed
 */
export const isSpecEmptyOrNotUpdated = (specList, allowEmpty = false) => {
  if (!specList || specList.length === 0) return true;
  if (allowEmpty) return checkIfSpecsInvalid(specList, true);
  return checkIfSpecsInvalid(specList) || !checkIfSpecsComplete(specList);
};

/** Build linked identifier fields from productfields/ API result. */
export const buildIdentifierFields = (result) => {
  const { fields, link_report: linkReport } = result;
  const fieldMap = Object.fromEntries(fields.map((field) => [field.json_field, field]));
  return linkReport.map((key) => fieldMap[key]).filter(Boolean);
};

/** Build linked identifier fields from environmentfields/ API result. */
export const buildEnvironmentIdentifierFields = (result) => {
  const {
    fields,
    link_pattern_fields: linkPatternFieldKeys,
    delimiter,
  } = result;
  if (!Array.isArray(fields) || fields.length === 0) return [];
  const fieldMap = Object.fromEntries(fields.map((field) => [field.json_field, field]));

  const jsonFieldKeys = [];
  (linkPatternFieldKeys || []).forEach((entry) => {
    if (entry && typeof entry === "object" && entry.json_field) {
      jsonFieldKeys.push(entry.json_field);
      return;
    }
    if (typeof entry !== "string" || !entry.trim()) return;

    if (delimiter) {
      const escapedDelimiter = delimiter.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      jsonFieldKeys.push(...entry.split(new RegExp(escapedDelimiter, "g")).filter(Boolean));
      return;
    }
    if (entry.includes(".")) {
      jsonFieldKeys.push(...entry.split(".").filter(Boolean));
      return;
    }
    jsonFieldKeys.push(entry);
  });

  return jsonFieldKeys.map((key) => fieldMap[key]).filter(Boolean);
};

/** Merge nested location_identifier with top-level getpoints/ fields. */
export const buildLocationIdentifierFromPoint = (point, identifierFields) => {
  const nested = point.location_identifier != null
    ? point.location_identifier
    : point.point_identifier;
  const identifier = nested && typeof nested === "object" && !Array.isArray(nested)
    ? { ...nested }
    : {};

  identifierFields.forEach((field) => {
    const jsonField = field.json_field;
    const topLevelValue = point[jsonField];
    const nestedValue = identifier[jsonField];
    const resolvedValue = topLevelValue != null && String(topLevelValue).trim()
      ? topLevelValue
      : nestedValue;
    if (resolvedValue != null && String(resolvedValue).trim()) {
      identifier[jsonField] = resolvedValue;
    }
  });

  return identifier;
};

export const getLocationIdentifier = (locationRow) => {
  let nested = null;
  if (locationRow.location_identifier != null) {
    nested = locationRow.location_identifier;
  } else if (locationRow.identifier && typeof locationRow.identifier === "object") {
    nested = locationRow.identifier;
  }
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) {
    return null;
  }
  return nested;
};

export const getLocationIdentifierFieldEntries = (locationRow, identifierFields) => {
  const mergedIdentifier = buildLocationIdentifierFromPoint(locationRow, identifierFields);

  if (identifierFields.length > 0) {
    const entries = identifierFields
      .map(({ json_field: jsonField, title_field: titleField }) => ({
        jsonField,
        titleField,
        value: mergedIdentifier[jsonField],
      }))
      .filter((entry) => entry.value != null && String(entry.value).trim());
    if (entries.length > 0) return entries;
  }

  if (typeof locationRow.identifier === "string" && locationRow.identifier.trim()) {
    return [{ jsonField: "identifier", titleField: "", value: locationRow.identifier.trim() }];
  }

  if (locationRow.location_id != null && String(locationRow.location_id).trim()) {
    return [{ jsonField: "location_id", titleField: "Location", value: String(locationRow.location_id) }];
  }

  return [];
};

export const formatLocationTitle = (locationRow, identifierFields) => {
  const entries = getLocationIdentifierFieldEntries(locationRow, identifierFields);
  if (entries.length === 0) {
    if (typeof locationRow.identifier === "string" && locationRow.identifier.trim()) {
      return locationRow.identifier.trim();
    }
    if (locationRow.location_id != null && String(locationRow.location_id).trim()) {
      return String(locationRow.location_id);
    }
    return "";
  }
  if (entries.length === 1 && !entries[0].titleField) {
    return entries[0].value;
  }
  return entries.map((entry) => (
    entry.titleField ? `${entry.titleField}: ${entry.value}` : entry.value
  )).join(" • ");
};

const normalizeLocationSpecStatus = (rawStatus) => {
  if (rawStatus === "added" || rawStatus === "specs_added") return "added";
  if (rawStatus === "pending" || rawStatus === "spec_incomplete") return "pending";
  return rawStatus || "pending";
};

/** Normalize a getpoints/ row into the location list row shape. */
export const mapEnvPointToLocationRow = (point, identifierFields) => {
  const locationIdentifier = buildLocationIdentifierFromPoint(point, identifierFields);

  const locationRow = {
    ...point,
    location_id: point.location_id,
    location_identifier: locationIdentifier,
  };

  let identifier = formatLocationTitle(locationRow, identifierFields);
  if (!identifier.trim() && point.location_id != null) {
    identifier = String(point.location_id);
  }

  const rawStatus = point.status != null ? point.status : point.spec_status;
  const status = normalizeLocationSpecStatus(rawStatus);

  return {
    ...locationRow,
    identifier,
    status,
  };
};

export const filterLocationRowsBySpecFilter = (locationRows, filter) => {
  if (!filter || filter === SPEC_FILTER_VALUES.ALL) return locationRows;
  return locationRows.filter((row) => row.status === filter);
};

/** Map env location rows to product-shaped rows for shared test-spec list panels. */
export const mapEnvLocationToProductShape = (locationRow, identifierFields = []) => ({
  ...locationRow,
  product_id: locationRow.location_id,
  product_identifier: buildLocationIdentifierFromPoint(locationRow, identifierFields),
  test_submitted: locationRow.test_submitted,
});

export const mapEnvLocationsToProductShape = (locations, identifierFields = []) => (
  locations.map((locationRow) => mapEnvLocationToProductShape(locationRow, identifierFields))
);

export const buildEnvGlobalSpecEditorRowsFromApi = (apiResult, testName) => (
  buildGlobalSpecEditorRowsFromApi(apiResult, testName)
);

/** Map envglobalspecs/ GET detail `locations` into location rows (identifier fields only). */
export const mapEnvGlobalSpecsPointsFromApi = (apiLocations, identifierFields) => {
  if (!Array.isArray(apiLocations)) return [];
  return apiLocations.map((point) => mapEnvPointToLocationRow(point, identifierFields));
};

export const buildEnvGlobalSpecsPostBody = ({
  testDisplayName,
  formattedFirstSpec,
  locationIdList,
  includeMrlCountriesList,
}) => {
  const test_spec = {
    min: formattedFirstSpec.min != null ? formattedFirstSpec.min : "",
    max: formattedFirstSpec.max != null ? formattedFirstSpec.max : "",
    unit: formattedFirstSpec.unit != null ? formattedFirstSpec.unit : "",
  };
  if (
    includeMrlCountriesList
    && Array.isArray(formattedFirstSpec.countries_lst)
    && formattedFirstSpec.countries_lst.length > 0
  ) {
    test_spec.countries_lst = formattedFirstSpec.countries_lst;
  }
  return {
    test_display_name: testDisplayName,
    test_spec,
    location_id_list: locationIdList,
  };
};

export const getIdentifierFieldKeys = (identifierFields) => (
  identifierFields.map((field) => field.json_field)
);

/**
 * Product identifier object shape depends on the API:
 * - specsmanager/ list rows use `identifier` (ESV SpecDisplayTable)
 * - getallproducts/ and globalspecs/ products use `product_identifier`
 */
export const getProductIdentifier = (productRow) => {
  if (productRow.identifier) {
    return productRow.identifier;
  }
  if (productRow.product_identifier) {
    return productRow.product_identifier;
  }
  throw new Error("Product row is missing identifier and product_identifier");
};

/**
 * Filter product rows by search term using per-company identifier field keys.
 */
export const filterSpecManagerProductsBySearch = (products, term, identifierFields) => {
  if (!Array.isArray(products)) return [];
  if (!term || !String(term).trim()) return products;
  const query = String(term).toLowerCase().trim();
  const keys = getIdentifierFieldKeys(identifierFields);
  return products.filter((product) => {
    const identifier = getProductIdentifier(product);
    if (keys.length > 0) {
      return keys.some((key) => {
        const val = identifier[key];
        return val != null && String(val).toLowerCase().includes(query);
      });
    }
    return Object.values(identifier).some(
      (val) => val != null && String(val).toLowerCase().includes(query),
    );
  });
};

export const filterSpecManagerLocationsBySearch = (locations, term, identifierFields) => {
  if (!Array.isArray(locations)) return [];
  if (!term || !String(term).trim()) return locations;
  const query = String(term).toLowerCase().trim();
  return locations.filter((locationRow) => {
    const title = formatLocationTitle(locationRow, identifierFields);
    return title.toLowerCase().includes(query);
  });
};

/**
 * Product has custom (product-specific) limits.
 * Global Test Spec API uses `specs_type: "custom"`. Other payloads may use `spec_type` or flags.
 */
export const isCustomSpecProduct = (product) => product.specs_type === "custom";

/**
 * Body for globalspecs/ POST with `update_operation` and `product_list`.
 * `user_payload`, `company_name`, and `company_domain` are appended by the axios request interceptor.
 *
 * @param {object} params
 * @param {string} params.testDisplayName
 * @param {{ min?: string, max?: string, unit?: string, countries_lst?: string[] }} params.formattedFirstSpec First row from {@link formatSpec}
 * @param {"standard_only"|"all_selected"} params.updateOperation
 * @param {unknown[]} params.products Selected globalspec products (must include `product_id`, `specs_type`)
 * @param {boolean} params.includeMrlCountriesList When true, copy selected MRL markets from `formattedFirstSpec.countries_lst`
 *   into `test_spec.countries_lst`. Must be true whenever the editor state includes non-empty `countries_lst` rows
 *   (same condition as {@link globalTestSpecEditorHasMrlCountryListData}); do not tie this to account permissions
 *   or saves can succeed while dropping MRL data the UI still shows.
 */
export const buildGlobalSpecsUpdateRequestBody = ({
  testDisplayName,
  formattedFirstSpec,
  updateOperation,
  products,
  includeMrlCountriesList,
}) => {
  const test_spec = {
    min: formattedFirstSpec.min != null ? formattedFirstSpec.min : "",
    max: formattedFirstSpec.max != null ? formattedFirstSpec.max : "",
    unit: formattedFirstSpec.unit != null ? formattedFirstSpec.unit : "",
  };
  if (
    includeMrlCountriesList
    && Array.isArray(formattedFirstSpec.countries_lst)
    && formattedFirstSpec.countries_lst.length > 0
  ) {
    test_spec.countries_lst = formattedFirstSpec.countries_lst;
  }
  const product_list = products
    .filter((p) => p && p.product_id)
    .map((p) => ({
      product_id: String(p.product_id),
      specs_type: isCustomSpecProduct(p) ? "custom" : "standard",
    }));

  return {
    test_display_name: testDisplayName,
    test_spec,
    update_operation: updateOperation,
    product_list,
  };
};

/**
 * Minimal fields from globalspecs detail `products[]` needed to preserve custom spec in the UI
 * when a product is removed and re-added from getallproducts (directory lacks specs_type/custom_specs).
 * @param {unknown} product
 * @returns {{ specs_type: string, custom_specs?: unknown }|null}
 */
export const snapshotGlobalspecCustomProductForRestore = (product) => {
  if (!product || typeof product !== "object" || !product.product_id) return null;
  if (!isCustomSpecProduct(product)) return null;
  const customSpecs = product.custom_specs;
  return {
    specs_type: product.specs_type,
    custom_specs: customSpecs != null && typeof customSpecs === "object" && !Array.isArray(customSpecs)
      ? { ...customSpecs }
      : customSpecs,
  };
};

/**
 * Merge stored globalspec custom snapshot onto a directory product row.
 * @param {unknown} product
 * @param {{ specs_type?: string, custom_specs?: unknown }|null|undefined} snapshot
 * @returns {unknown}
 */
export const applyGlobalspecCustomSnapshotToProduct = (product, snapshot) => {
  if (!product || typeof product !== "object" || !product.product_id) return product;
  if (!snapshot || !isCustomSpecProduct(snapshot)) return product;
  return {
    ...product,
    specs_type: snapshot.specs_type,
    custom_specs: snapshot.custom_specs != null && typeof snapshot.custom_specs === "object" && !Array.isArray(snapshot.custom_specs)
      ? { ...snapshot.custom_specs }
      : snapshot.custom_specs,
  };
};

/**
 * Single-line label from globalspecs product `custom_specs` `{ min, max, unit }`.
 * @param {{ min?: string, max?: string, unit?: string }} [customSpecs]
 * @returns {string}
 */
export const formatCustomSpecsFromApi = (customSpecs) => {
  if (!customSpecs || typeof customSpecs !== "object") return "";
  const min = String(customSpecs.min != null ? customSpecs.min : "").trim();
  const max = String(customSpecs.max != null ? customSpecs.max : "").trim();
  const unit = String(customSpecs.unit != null ? customSpecs.unit : "").trim();

  let core = "";
  if (min && max) core = `${min} - ${max}`;
  else if (min) core = min;
  else if (max) core = max;

  if (!core && !unit) return "";
  if (!core) return unit;
  return unit ? `${core} ${unit}` : core;
};

/** EditSpec global Test Spec — preview row labels (MRL vs standard). */
export const formatPreviewTestType = (spec = {}) => {
  const isMRL = Array.isArray(spec.countries_lst) && spec.countries_lst.length > 0;
  if (isMRL) return "MRL";
  return getTestTypeDisplayLabel(spec.test_type) || "Not defined";
};

/** EditSpec global Test Spec — preview value column (MRL uses subtitle helper elsewhere). */
export const formatPreviewTestSpec = (spec = {}) => {
  const isMRL = Array.isArray(spec.countries_lst) && spec.countries_lst.length > 0;
  if (isMRL) return "Not defined";
  return formatSpecValueFromEditorShape(spec);
};

/**
 * Step 2 / footer "Preview Selection" gating for global test spec flow only.
 */
export const computeTestSpecPreviewAdvanceState = ({
  loading,
  dataOfSpecEditing = [],
  testSpecEditHasDraftChanges = true,
  selectedProductCount,
  testSpecAddCountFromAllList,
  testSpecSelectedCount,
  testSpecAllProductsLoading,
  /** When false, footer must not bypass draft/baseline gates (avoids add-products / preview soft-lock). */
  testSpecHasStep1Baseline = true,
}) => {
  const selectedCountForGate = selectedProductCount != null
    ? selectedProductCount
    : testSpecSelectedCount;

  const previewSpecIncomplete = dataOfSpecEditing.length > 0
    && isSpecEmptyOrNotUpdated(dataOfSpecEditing, false);
  const hasMrlCountries = dataOfSpecEditing.some(
    (item) => item && item.countries_lst && Array.isArray(item.countries_lst) && item.countries_lst.length > 0,
  );
  const previewSpecBlocksFooter = (previewSpecIncomplete && !hasMrlCountries)
    || isGlobalTestSpecMrlSaveBlocked(dataOfSpecEditing);
  const isAddProductsMode = testSpecAddCountFromAllList > 0;
  const previewFooterLoading = loading || testSpecAllProductsLoading;
  let previewPrimaryDisabled;
  if (previewFooterLoading) {
    previewPrimaryDisabled = true;
  } else if (isAddProductsMode && testSpecHasStep1Baseline) {
    previewPrimaryDisabled = false;
  } else if (isAddProductsMode) {
    previewPrimaryDisabled = true;
  } else {
    const noProductsForPreview = selectedCountForGate === 0;
    const noSpecRows = !dataOfSpecEditing || dataOfSpecEditing.length === 0;
    previewPrimaryDisabled = noProductsForPreview
      || noSpecRows
      || previewSpecBlocksFooter
      || !testSpecEditHasDraftChanges;
  }

  let previewSelectionTooltip = "";
  if (isAddProductsMode && testSpecHasStep1Baseline) {
    previewSelectionTooltip = "";
  } else if (isAddProductsMode) {
    previewSelectionTooltip = "Editor is still initializing; try again in a moment";
  } else if (selectedCountForGate === 0) {
    previewSelectionTooltip = "Please select at least one product";
  } else if (!dataOfSpecEditing || dataOfSpecEditing.length === 0) {
    previewSelectionTooltip = "No test spec to preview";
  } else if (previewSpecBlocksFooter) {
    if (isGlobalTestSpecMrlSaveBlocked(dataOfSpecEditing)) {
      if (checkIfSpecsInvalid(dataOfSpecEditing)) {
        previewSelectionTooltip = "Missing or invalid inputs";
      } else if (globalTestSpecMrlMissingSelectedMarkets(dataOfSpecEditing)) {
        previewSelectionTooltip = "Please select at least one country for MRL tests";
      }
    } else {
      previewSelectionTooltip = "Missing or invalid inputs";
    }
  } else if (!testSpecEditHasDraftChanges) {
    previewSelectionTooltip = "No change(s) made";
  }

  return {
    previewPrimaryDisabled,
    previewSelectionTooltip,
    previewFooterLoading,
    isAddProductsMode,
  };
};

export const getProductIdentifierFieldEntries = (productRow, identifierFields) => {
  const identifier = getProductIdentifier(productRow);
  return identifierFields
    .map(({ json_field: jsonField, title_field: titleField }) => ({
      jsonField,
      titleField,
      value: identifier[jsonField],
    }))
    .filter((entry) => entry.value != null && String(entry.value).trim());
};

export const formatProductTitle = (productRow, identifierFields) => {
  const entries = getProductIdentifierFieldEntries(productRow, identifierFields);
  return entries.map((entry) => `${entry.titleField}: ${entry.value}`).join(" • ");
};
